import * as Api from '@ViewModels';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { actionItemQueryKeys } from './actionItemQueryKeys';

const PAGE_SIZE = 100;
const MAX_PAGES = 10;

export interface IOpenActionItemsResult {
	items: Api.IActionItem[];
	/** True when the page cap was hit and the server still had more. */
	hasMore: boolean;
}

export function buildOpenActionItemsFilter({
	assignedTo,
	search,
	completed,
	contactId,
}: {
	assignedTo: string | undefined;
	search: string;
	completed: boolean;
	/** Only action items that reference this contact. */
	contactId?: string | null;
}): Api.IRichContentRequest {
	const criteria: Api.IRichContentRequest['criteria'] = [
		{ property: Api.RichContentProperty.IsCompleted, value: completed ? 'true' : 'false' },
		{ property: Api.RichContentProperty.Type, value: 'ActionItem' },
	];
	if (assignedTo) {
		criteria.push({ property: Api.RichContentProperty.AssignedTo, value: assignedTo });
	}
	const trimmedSearch = search.trim();
	if (trimmedSearch) {
		criteria.push({ property: Api.RichContentProperty.Content, value: trimmedSearch });
	}
	if (contactId) {
		criteria.push({ property: Api.RichContentProperty.ReferencedContact, value: contactId });
	}
	return { criteria };
}

export async function fetchAllOpenActionItems({
	userSession,
	filter,
	impersonationContext,
}: {
	userSession: Api.UserSessionContext;
	filter: Api.IRichContentRequest;
	impersonationContext?: Api.IImpersonationContext;
}): Promise<IOpenActionItemsResult> {
	const items: Api.IActionItem[] = [];
	let pageToken: string | undefined = '';
	let pagesLoaded = 0;
	while (pagesLoaded < MAX_PAGES) {
		const page: Api.IPagedCollection<Api.IActionItem> = await userSession.webServiceHelper.callAsync<
			Api.IPagedCollection<Api.IActionItem>
		>(
			Api.ImpersonationBroker.composeApiUrl({
				impersonationContext,
				queryParams: {
					currentUserOnly: false,
					pageSize: PAGE_SIZE,
					pageToken,
					sortBy: 'dueDate',
					sort: 'asc',
				},
				urlPath: 'actionItem/filter',
			}),
			'POST',
			filter
		);
		items.push(...(page.values ?? []));
		pagesLoaded += 1;
		pageToken = page.pageToken;
		if (!pageToken) {
			return { items, hasMore: false };
		}
	}
	return { items, hasMore: true };
}

export function useOpenActionItemsQuery({
	assignedTo,
	search,
	contactId,
	impersonationContext,
	enabled = true,
}: {
	/** Restrict to one assignee; undefined returns everything visible to the current user. */
	assignedTo: string | undefined;
	search: string;
	contactId?: string | null;
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
}) {
	const userSession = useUserSession();
	return useQuery<IOpenActionItemsResult>({
		enabled,
		placeholderData: keepPreviousData,
		queryKey: actionItemQueryKeys.open(assignedTo, search.trim(), impersonationContext?.account?.id, contactId),
		queryFn: () =>
			fetchAllOpenActionItems({
				userSession,
				filter: buildOpenActionItemsFilter({ assignedTo, search, completed: false, contactId }),
				impersonationContext,
			}),
		meta: { toastErrorMessage: 'Failed to load action items' },
	});
}
